/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Grid, Box, Typography, Slide, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { withTransaction } from '@elastic/apm-rum-react';
import { debounce } from 'lodash';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import { commonSlice } from '../../state/reducers/common';
import ReactMuiTableListView from '../../components/Common/TableComponent/TableComponent';
import useOrganization from '../../hooks/UseOrganization';
import CreateEditOrgModal from '../../components/Organization/CreateEditOrgModal';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import ReactMuiTableColumnHeaderTextEllipsis from '../../components/Common/ReactMuiTableColumnHeaderTextEllipsis';
import ChangeStatusDialog from '../../components/Common/ChangeStatusDialog';
import {
  CommonTableCell,
  DateTimeCell,
  StatusCell,
} from '../../components/Common/CommonTableCell';
import utils from '../../utils/Utils';
import SearchBox from '../../components/Common/SearchBox';

/**
 * @function Dashboard
 * @description This component displays a dashboard containing a list of organizations.
 * @returns {JSX} renders the Dashboard component UI
 * @example <Dashboard />
 */
const Dashboard = () => {
  const theme = useTheme();
  // const caseInsensitiveSortNotCreated = (rowA, rowB) => {
  //   const extractValue = (row) => {
  //     const clients = row?.original?.clients ?? [];
  //     if (clients.length > 0) {
  //       return clients
  //         .map((item) => item?.product?.name)
  //         .join(', ')
  //         .toLowerCase();
  //     }
  //     return 'Not created yet'.toLowerCase();
  //   };

  //   const a = extractValue(rowA);
  //   const b = extractValue(rowB);

  //   if (a > b) {
  //     return 1;
  //   }
  //   if (a < b) {
  //     return -1;
  //   }
  //   return 0;
  // };

  const tableColumns = useMemo(
    () => [
      {
        heading: 'Organization ID',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'name',
        Cell: CommonTableCell,
        width: 150,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'Display Name',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'display_name',
        Cell: CommonTableCell,
        width: 150,
        sortType: utils.caseInsensitiveSort,
      },
      // {
      //   heading: 'Application Access',
      //   Header: ReactMuiTableColumnHeaderTextEllipsis,
      //   accessor: 'clients',
      //   Cell: applicationAccessCell,
      //   width: 150,
      //   sortType: caseInsensitiveSortNotCreated,
      // },
      {
        heading: 'Onboarded On',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'created_at',
        Cell: DateTimeCell,
        width: 150,
      },
      {
        heading: 'Status',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'is_active',
        Cell: StatusCell,
        sortType: utils.statusSort,
        width: 150,
      },
    ],
    [utils]
  );

  // const [deleteOrganization, setDeleteOrganization] = useState({});
  // const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQueryText, setSearchQueryText] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [openEditOrgModal, setOpenEditOrgModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState({});
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);
  const {
    organizationDetailsList,
    updateOrganization,
    // organizationDelete,
    organizationListPageCount,
    totalOrganizationList,
    organizationListData,
  } = useOrganization();
  const shouldRefreshOrgList = useSelector(
    (state) => state.common.shouldRefreshOrgList
  );
  const orgId = useSelector((state) => state.common.orgId);

  /**
   * @function getRowPropsFunc
   * @description Returns row properties for the specified row and theme, including onClick behavior and styling.
   * @param {Object} row - The row object representing a project.
   * @param {Object} theme - The theme object.
   * @returns {Object} - Row properties with onClick behavior and styling.
   */
  const getRowPropsFunc = useCallback(
    (row, themes) => {
      return {
        style: {
          backgroundColor:
            row?.original?.id === orgId
              ? `${themes?.palette?.other?.listTableRowSelectedBg}`
              : 'unset',
          position: 'relative',
        },
      };
    },
    [orgId]
  );

  useEffect(() => {
    setIsLoading(true);
    organizationDetailsList(pageIndex, pageSize, searchTerm).then(() =>
      setIsLoading(false)
    );
  }, [shouldRefreshOrgList]);

  useEffect(() => {
    if (pageIndex >= 0 && pageSize > 0) {
      organizationDetailsList(pageIndex, pageSize, searchTerm).then((resp) => {
        setPageIndex(Number(resp?.data?.page) - 1);
      });
    }
  }, [pageIndex, pageSize, shouldRefreshOrgList]);

  useEffect(() => {
    organizationDetailsList(0, pageSize, searchTerm);
  }, [searchTerm, shouldRefreshOrgList]);

  /**
   * @function onTableStateChangeMethod
   * @description helps in setting the pageIndex and pageSize
   * @param {object} pageIndex - pageIndex method of the table.
   * @returns {void} - updates the pageIndex and pageSize.
   * @example onTableStateChangeMethod(pageIndex)
   */
  const onTableStateChangeMethod = useCallback(
    (pageIndexData) => {
      setPageIndex(pageIndexData.pageIndex);
      setPageSize(pageIndexData.pageSize);
    },
    [setPageIndex, setPageSize]
  );

  /**
   * @function debouncedSave
   * @description debounce function to update the search term
   * @returns {void} sets the the searchTerm value
   * @example debouncedSave()
   */
  const debouncedSave = useCallback(
    debounce((data) => {
      setSearchTerm(data);
    }, 800),
    []
  );

  /**
   * @function searchOrganizationByName
   * @description Updates the search term state, which triggers the filtering of the organization list based on search input.
   * @param {Object} e - The event object.
   * @param {string} e.target.value - The value of the search input field.
   * @returns {void}
   * @example searchOrganizationByName(event)
   */

  const searchOrganizationByName = useCallback(
    (e) => {
      debouncedSave(e.target.value);
    },
    [debouncedSave]
  );

  /**
   * @function handleSetSearchText
   * @description handles the input field of searchText.
   * @param {object} searchText - the current text is passed from user for searching.
   * @returns {void} sets the searchText value
   * @example handleSetSearchText(searchText)
   */

  const handleSetSearchText = useCallback(
    (searchText) => {
      setSearchQueryText(searchText);
    },
    [setSearchQueryText]
  );

  /**
   * @function handleDisableOrganization
   * @description Toggles the active status of an organization.
   * @param {Object} payload - The payload containing the updated status.
   * @param {number} id - ID of the organization to update.
   * @param {number} orgName - The ID of the organization to which the application belongs.
   * @param {number} pageIndex - The page page index of current page.
   * @param {number} pageSize - The page size of current page.
   * @param {number} searchTerm - search term for filtring the data.
   * @returns {void}
   * @example handleDisableOrganization(payload, id);
   */
  const handleDisableOrganization = useCallback(
    (selectedOrgData, pageIndexData, pageSizeData, searchTermData) => {
      const payload = {
        is_active: !selectedOrgData?.is_active,
        id: selectedOrgData?.id,
        name: selectedOrgData?.name,
        display_name: selectedOrgData?.display_name,
        created_at: selectedOrgData?.created_at,
        contact_email: selectedOrgData?.contact_email,
        contact_number: selectedOrgData?.contact_number,
      };
      updateOrganization(payload, selectedOrgData?.id).then(() => {
        organizationDetailsList(
          pageIndexData,
          pageSizeData,
          searchTermData
        ).then((resp) => {
          setPageIndex(Number(resp?.data?.page) - 1);
        });
        setShowStatusChangeModal(false);
      });
    },
    [
      updateOrganization,
      organizationDetailsList,
      setPageIndex,
      setShowStatusChangeModal,
    ]
  );

  const menus = React.useMemo(
    () => [
      {
        label: 'Edit',
        id: 'edit',
        icon: EditIcon,
        onClick: ({ props }) => {
          setSelectedOrg(props?.original);
          setOpenEditOrgModal(true);
        },
      },
      {
        label: 'Change Status',
        id: 'disable',
        icon: ChangeCircleIcon,
        onClick: ({ props }) => {
          setShowStatusChangeModal(true);
          setSelectedOrg(props?.original);
        },
      },
    ],
    [pageIndex, pageSize, searchTerm, commonSlice]
  );

  const handleCloseStatusChangeModal = useCallback(() => {
    setShowStatusChangeModal(false);
  }, [setShowStatusChangeModal]);

  const handleChangeStatus = useCallback(() => {
    handleDisableOrganization(selectedOrg, pageIndex, pageSize, searchTerm);
  }, [handleDisableOrganization, selectedOrg, pageIndex, pageSize, searchTerm]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box
          margin="20px"
          display="flex"
          flexDirection="column"
          overflow="auto"
        >
          <Typography variant="h3">All Organization</Typography>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            mt={2}
          >
            <Box width="200px">
              <SearchBox
                placeholder="Search"
                variant="filled"
                value={searchQueryText}
                onChange={searchOrganizationByName}
                onInput={(e) => handleSetSearchText(e.target.value)}
                aria-label="Search organization"
              />
            </Box>
            {/* <Button
              variant="contained"
              onClick={() => {
                navigate('/dashboard/organization', {
                  state: { mode: 'create' },
                });
              }}
            >
              + Create Organization
            </Button> */}
          </Box>
          <Box flexGrow={1} display="flex" flexDirection="column">
            {isLoading ? (
              <Box
                flexGrow={1}
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                height="50vh"
              >
                <Typography variant="h6" textAlign="center" sx={{ pt: '10px' }}>
                  Loading...
                </Typography>
              </Box>
            ) : (
              <Slide in={true} direction="up" mountOnEnter timeout={500}>
                {organizationListData?.length > 0 ? (
                  <Box pt={2}>
                    <ReactMuiTableListView
                      data={organizationListData}
                      columns={tableColumns}
                      onTableStateChange={onTableStateChangeMethod}
                      getHeaderProps={() => ({
                        style: {
                          display: 'flex',
                          alignItems: 'center',
                        },
                      })}
                      getRowProps={getRowPropsFunc}
                      rowLevelRightClickMenus={menus}
                      enableRowSelection={true}
                      pageCount={Number(organizationListPageCount)}
                      dataLength={Number(totalOrganizationList)}
                      enablePagination={true}
                      initialPageSize={5}
                      rowsPerPageOptions={[5, 10, 15, 20]}
                      enableManualFilter={true}
                      enableControlledPagination={true}
                      showPaginationAction={false}
                      skipPageReset={true}
                      searchTerm={searchTerm}
                    />
                  </Box>
                ) : (
                  <Box
                    flexGrow={1}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                    height="calc(100vh - 438px)"
                    overflow="auto"
                  >
                    {searchTerm ? (
                      <NoDataImage
                        style={{ color: theme.palette.primary.main }}
                      />
                    ) : (
                      <NoDataCreated
                        style={{ color: theme.palette.primary.main }}
                      />
                    )}
                    <Typography
                      variant="subtitle1"
                      textAlign="center"
                      sx={{ pt: '10px' }}
                    >
                      {searchTerm
                        ? 'No matching result found.'
                        : 'No organizations found. Please create an organization to proceed.'}
                    </Typography>
                  </Box>
                )}
              </Slide>
            )}
          </Box>
        </Box>
      </Grid>
      <ChangeStatusDialog
        open={showStatusChangeModal}
        onCloseClick={handleCloseStatusChangeModal}
        onChangeClick={handleChangeStatus}
        confirmationMsg={`Are you sure you want to change the status of "${selectedOrg?.name}" to "${selectedOrg?.is_active ? 'Inactive' : 'Active'}"?`}
      />
      {openEditOrgModal && (
        <CreateEditOrgModal
          selectedOrg={selectedOrg?.id}
          stateOfForm="edit"
          open={openEditOrgModal}
          setOpen={setOpenEditOrgModal}
          organizationDetailsList={organizationDetailsList}
          pageIndexData={pageIndex}
          pageSizeData={pageSize}
          searchTermData={searchTerm}
        />
      )}
    </Grid>
  );
};

export default withTransaction('Dashboard', 'component')(Dashboard);
